/**
 * EditableSetCard - Set editor card within the package structure editor (C5)
 *
 * Displays a set with editable name, selectionRule, and product list.
 * Supports adding/removing products and editing feature values.
 */
import React, { useState } from "react";
import type {
  EnrichedPackageStructureProduct,
  SelectionRule,
  LeafProductPickerItem,
} from "../../../../types/adminBilling";
import {
  SELECTION_RULE_LABELS,
  ALL_SELECTION_RULES,
} from "./constants";
import { EditableProductRow } from "./EditableProductRow";
import type { EditorSet } from "./PackageStructureEditor";

export interface EditableSetCardProps {
  set: EditorSet;
  setIndex: number;
  leafProducts: LeafProductPickerItem[];
  usedProductIds: Set<string>;
  onUpdate: (updated: EditorSet) => void;
  onRemove: () => void;
}

export const EditableSetCard: React.FC<EditableSetCardProps> = ({
  set,
  setIndex,
  leafProducts,
  usedProductIds,
  onUpdate,
  onRemove,
}) => {
  const [addingProduct, setAddingProduct] = useState(false);
  const [pickerKey, setPickerKey] = useState(0);

  const availableProducts = leafProducts.filter(
    (lp) => !usedProductIds.has(lp.id)
  );

  const handleNameChange = (name: string) => {
    onUpdate({ ...set, name });
  };

  const handleRuleChange = (selectionRule: SelectionRule) => {
    onUpdate({ ...set, selectionRule });
  };

  const handleAddProduct = (productId: string) => {
    const leaf = leafProducts.find((lp) => lp.id === productId);
    if (!leaf) return;

    const newProduct: EnrichedPackageStructureProduct = {
      productId: leaf.id,
      productName: leaf.name,
      isDefault: false,
    };
    onUpdate({ ...set, products: [...set.products, newProduct] });
    // Reset picker to empty selection but keep it open for adding more
    setPickerKey((k) => k + 1);
  };

  const handleRemoveProduct = (index: number) => {
    const updated = [...set.products];
    updated.splice(index, 1);
    onUpdate({ ...set, products: updated });
  };

  const handleToggleDefault = (index: number) => {
    const updated = set.products.map((p, i) =>
      i === index ? { ...p, isDefault: !p.isDefault } : p
    );
    onUpdate({ ...set, products: updated });
  };

  const handleFeatureValueChange = (
    productIndex: number,
    featureCode: string,
    value: string | number | boolean
  ) => {
    const updated = set.products.map((p, i) => {
      if (i !== productIndex) return p;
      const featureValues = { ...(p.featureValues ?? {}), [featureCode]: value };
      return { ...p, featureValues };
    });
    onUpdate({ ...set, products: updated });
  };

  const handleRemoveSet = () => {
    if (set.products.length > 0) {
      const confirmed = window.confirm(
        `Usunąć set "${set.name || `Set ${setIndex + 1}`}" z ${set.products.length} produktami?`
      );
      if (!confirmed) return;
    }
    onRemove();
  };

  return (
    <div className="bg-neutral-50 rounded-md border border-neutral-200 p-4">
      {/* Set header: name, rule, remove */}
      <div className="flex items-start gap-3 mb-3">
        <div className="flex-1">
          <label className="block text-xs font-medium text-neutral-700 mb-1">
            Nazwa setu
          </label>
          <input
            type="text"
            value={set.name}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder={`Set ${setIndex + 1}`}
            className={`w-full border rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 ${
              !set.name.trim() ? "border-red-300" : "border-neutral-300"
            }`}
          />
          {!set.name.trim() && (
            <p className="text-red-600 text-xs mt-0.5">Nazwa setu jest wymagana</p>
          )}
        </div>
        <div className="w-48">
          <label className="block text-xs font-medium text-neutral-700 mb-1">
            Reguła wyboru
          </label>
          <select
            value={set.selectionRule}
            onChange={(e) =>
              handleRuleChange(e.target.value as SelectionRule)
            }
            className="w-full border border-neutral-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            {ALL_SELECTION_RULES.map((rule) => (
              <option key={rule} value={rule}>
                {SELECTION_RULE_LABELS[rule]}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          onClick={handleRemoveSet}
          aria-label={`Usuń set: ${set.name || `Set ${setIndex + 1}`}`}
          className="mt-5 text-xs text-red-600 hover:text-red-800 px-2 py-1 flex-shrink-0"
        >
          Usuń set
        </button>
      </div>

      {/* Products in set */}
      <div className="space-y-2">
        {set.products.map((product, pIndex) => (
          <EditableProductRow
            key={product.productId || pIndex}
            product={product}
            leafProduct={leafProducts.find(
              (lp) => lp.id === product.productId
            )}
            onRemove={() => handleRemoveProduct(pIndex)}
            onToggleDefault={() => handleToggleDefault(pIndex)}
            onFeatureValueChange={(featureCode, val) =>
              handleFeatureValueChange(pIndex, featureCode, val)
            }
          />
        ))}
      </div>

      {/* Add product button/dropdown (C6) */}
      {addingProduct ? (
        <div className="mt-2 flex items-center gap-2">
          {availableProducts.length > 0 ? (
            <select
              key={pickerKey}
              defaultValue=""
              onChange={(e) => {
                if (e.target.value) handleAddProduct(e.target.value);
              }}
              className="flex-1 border border-neutral-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="" disabled>
                -- wybierz produkt --
              </option>
              {availableProducts.map((lp) => (
                <option key={lp.id} value={lp.id}>
                  {lp.name}
                </option>
              ))}
            </select>
          ) : (
            <span className="text-xs text-neutral-400 italic">
              Brak dostępnych produktów
            </span>
          )}
          <button
            type="button"
            onClick={() => setAddingProduct(false)}
            className="text-xs text-neutral-500 hover:text-neutral-700 px-2 py-1"
          >
            Anuluj
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setAddingProduct(true)}
          disabled={availableProducts.length === 0 && !addingProduct}
          title={
            leafProducts.length === 0
              ? "Brak aktywnych produktów LEAF"
              : availableProducts.length === 0
                ? "Wszystkie dostępne produkty zostały już dodane"
                : undefined
          }
          className="mt-2 text-xs text-primary-600 hover:text-primary-800 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          + Dodaj produkt ({set.products.length})
        </button>
      )}
    </div>
  );
};
